import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, PaperPlaneRight, CircleNotch, ChatCircleText, Plus, Question, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { RuleCard } from "./RuleCard";

const EXAMPLES = [
  "If a chassis number ends in 24 and is an Adventure, that is a Generation 1 van.",
  "Adventure Generation 1 specs are: battery 200Ah Projecta Lithium, solar 2 × 200W …",
  "If the notes mention 4 x 200W solar panels, set solar to 800W (4 × 200W).",
];

function Proposal({ rule, vanCounts, ...rest }) {
  if (!rule) return <div className="text-xs text-neutral-600 italic">Rule removed.</div>;
  if (rule.status === "approved") {
    return <div className="flex items-center gap-2 text-xs text-[#73bf44] rounded-lg border border-[#73bf44]/25 px-3 py-2"><CheckCircle size={14} weight="fill" /> Approved: {rule.title}</div>;
  }
  return <RuleCard rule={{ ...rule, van_count: rule.van_count ?? vanCounts?.[rule.id] }} compact {...rest} />;
}

function Message({ m, rules, onAnswer, cardProps }) {
  if (m.role === "admin") {
    return <div className="ml-8 rounded-2xl rounded-tr-sm bg-[#73bf44]/10 border border-[#73bf44]/20 px-4 py-2.5 text-sm text-neutral-100 whitespace-pre-wrap" data-testid="assistant-admin-msg">{m.text}</div>;
  }
  if (m.status === "thinking") {
    return <div className="flex items-center gap-2 text-sm text-neutral-400" data-testid="assistant-thinking"><CircleNotch size={15} className="animate-spin text-[#73bf44]" /> Reading your rules and the schedule…</div>;
  }
  return (
    <div className="space-y-2.5" data-testid="assistant-reply">
      <div className="flex gap-2 text-sm text-neutral-200"><Sparkle size={16} weight="fill" className="text-[#73bf44] shrink-0 mt-0.5" /><span>{m.text}</span></div>
      {(m.errors || []).map((e, i) => <div key={i} className="flex gap-2 text-xs text-red-300"><WarningCircle size={14} className="shrink-0 mt-px" /> {e}</div>)}
      {(m.rule_ids || []).map((id) => <Proposal key={id} rule={rules[id]} {...cardProps} />)}
      {(m.questions || []).length > 0 && (
        <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-3 space-y-2" data-testid="assistant-questions">
          {m.questions.map((q, i) => (
            <button key={i} onClick={() => onAnswer(q)} className="flex gap-2 text-left text-xs text-sky-200 hover:text-white">
              <Question size={14} className="shrink-0 mt-px" /> {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AssistantPanel({ rev, prefill, onPrefillUsed, onChanged, cardProps }) {
  const [sid, setSid] = useState(null);
  const [chat, setChat] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);

  const load = useCallback((id) => adminApi.get(`/admin/rulebook/assistant/${id}`).then(({ data }) => { setChat(data); return data; }), []);
  useEffect(() => { adminApi.get("/admin/rulebook/assistant").then(({ data }) => { if (data.session_id) { setSid(data.session_id); load(data.session_id); } }).catch(() => {}); }, [load]);
  useEffect(() => { if (prefill) { setText(prefill); onPrefillUsed(); } }, [prefill, onPrefillUsed]);
  useEffect(() => { if (rev && sid) load(sid).catch(() => {}); }, [rev]); // eslint-disable-line react-hooks/exhaustive-deps
  const thinking = chat?.messages?.some((m) => m.status === "thinking");
  useEffect(() => {
    if (!thinking || !sid) return undefined;
    const t = setInterval(() => load(sid).then((d) => { if (!d.messages.some((m) => m.status === "thinking")) onChanged(); }), 2500);
    return () => clearInterval(t);
  }, [thinking, sid, load, onChanged]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [chat?.messages?.length, thinking]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await adminApi.post("/admin/rulebook/assistant", { session_id: sid, message: text });
      setSid(data.session_id); setText("");
      await load(data.session_id);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSending(false); }
  };
  const fresh = () => { setSid(null); setChat(null); };
  const wrapped = { ...cardProps, onApprove: async (r) => { await cardProps.onApprove(r); sid && load(sid); }, onDelete: async (r) => { await cardProps.onDelete(r); sid && load(sid); } };

  return (
    <aside className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl flex flex-col xl:sticky xl:top-4 h-[640px] xl:h-[calc(100vh-2rem)]" data-testid="rules-assistant">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-neutral-800">
        <div className="w-8 h-8 rounded-lg bg-[#73bf44]/10 flex items-center justify-center"><Sparkle size={17} weight="fill" className="text-[#73bf44]" /></div>
        <div className="flex-1"><div className="font-heading text-sm font-semibold text-white">Rules Assistant</div><div className="text-[0.65rem] text-neutral-500">Describe rules in plain English. Nothing runs until you approve it.</div></div>
        <button onClick={fresh} data-testid="assistant-new" className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white"><Plus size={12} /> New chat</button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="assistant-messages">
        {!chat?.messages?.length && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-neutral-400"><ChatCircleText size={16} /> Try something like:</div>
            {EXAMPLES.map((e) => <button key={e} onClick={() => setText(e)} className="block w-full text-left text-xs text-neutral-300 rounded-lg border border-neutral-800 hover:border-[#73bf44]/50 px-3 py-2">{e}</button>)}
            <p className="text-xs text-neutral-600 pt-2">You can paste a whole list or table at once. The assistant asks if anything is unclear.</p>
          </div>
        )}
        {(chat?.messages || []).map((m) => <Message key={m.id} m={m} rules={chat.rules || {}} cardProps={wrapped} onAnswer={(q) => setText(`Re: “${q}”\n`)} />)}
        <div ref={bottom} />
      </div>
      <div className="p-3 border-t border-neutral-800">
        <div className="flex items-end gap-2 bg-black/40 border border-neutral-700 rounded-xl px-3 py-2 focus-within:border-[#73bf44]">
          <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            rows={3} placeholder="e.g. Any of these models are single axle: … Everything else is dual." data-testid="assistant-input"
            className="flex-1 bg-transparent outline-none resize-none text-sm text-white placeholder:text-neutral-600" />
          <button onClick={send} disabled={!text.trim() || sending || thinking} data-testid="assistant-send" className="p-2.5 rounded-full bg-[#73bf44] text-[#050505] disabled:opacity-40 hover:bg-[#5fa338]">
            {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} weight="fill" />}
          </button>
        </div>
        <div className="text-[0.6rem] text-neutral-600 mt-1.5 px-1">Ctrl/⌘ + Enter to send</div>
      </div>
    </aside>
  );
}
